// PRD-13: show a product photo to a customer. On a phone this opens the system share sheet with the picture and
// a line of text; where sharing is not available (a desktop browser) the picture is downloaded instead.
export type ShareResult = 'shared' | 'cancelled' | 'downloaded' | 'failed';

export interface ShareInput {
  url: string;
  name: string;
  text?: string;
}

const slug = (s: string) =>
  s
    .toLowerCase()
    .replace(/[^\p{L}\p{N}]+/gu, '-')
    .replace(/^-+|-+$/g, '')
    .slice(0, 40) || 'product';

export async function shareProductPhoto({ url, name, text }: ShareInput): Promise<ShareResult> {
  let blob: Blob;
  try {
    blob = await (await fetch(url)).blob();
  } catch {
    return 'failed';
  }
  const ext = blob.type === 'image/webp' ? 'webp' : 'jpg';
  const file = new File([blob], `${slug(name)}.${ext}`, { type: blob.type || 'image/jpeg' });
  try {
    if (typeof navigator.share === 'function') {
      if (navigator.canShare?.({ files: [file] })) {
        await navigator.share({ files: [file], title: name, text: text ?? name });
      } else {
        await navigator.share({ title: name, text: text ?? name });
      }
      return 'shared';
    }
  } catch (e) {
    if (e instanceof DOMException && e.name === 'AbortError') return 'cancelled';
    // Any other failure falls through to the download below.
  }
  const href = URL.createObjectURL(file);
  const a = document.createElement('a');
  a.href = href;
  a.download = file.name;
  document.body.append(a);
  a.click();
  a.remove();
  setTimeout(() => URL.revokeObjectURL(href), 10_000);
  return 'downloaded';
}
